
import Vue from 'vue'
import { VIcon, VMenu, VTextField, VTimePicker } from 'vuetify/lib'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
dayjs.extend(localizedFormat)
import { VuetifyIcon } from 'vuetify/types/services/icons'
import JcdcProgressLinear from '@/components/jcdc/JcdcProgressLinear.vue'

export default Vue.extend({
  name: 'JcdcTimePicker',
  components: {
    VMenu,
    VTextField,
    VTimePicker,
    VIcon,
    JcdcProgressLinear,
  },
  props: {
    attach: { type: Boolean, default: false },
    color: { type: String, default: 'primary' },
    label: { type: String, required: true },
    required: { type: Boolean, default: false },
    minTime: { type: [String, Date, Number, Object], default: '' },
    maxTime: { type: [String, Date, Number, Object], default: '' },
    hint: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    defaultValue: { type: String, default: '' },
    dense: { type: Boolean, default: false },
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      error: false,
      time24h: this.defaultValue,
      time12h: '',
      menu: this.value, // Menu state
      persistentHint: false,
      invalidHint: '',
      colorValue: this.color,
    }
  },
  computed: {
    computedRules(): Array<(data: string) => void> {
      // Rules Array
      const ruleArray: Array<(data: string) => void> = []
      // Required
      if (this.required) {
        ruleArray.push(
          (v: string) => Boolean(v) || `${this.label} is required.`
        )
      }
      // Single Time Picker
      ruleArray.push(
        (v: string) =>
          (v ? dayjs(`2020-01-01 ${v}`, 'LT').isValid() : !v) ||
          `Please enter a valid time (ex. 12:30 AM).`
      )
      return ruleArray
    },
    timeHint(): string {
      const formattedMinTime = this.convert24to12(this.minTime),
        formattedMaxTime = this.convert24to12(this.maxTime)

      // Hint for Min/Max times
      if (this.hint) return this.hint
      if (this.minTime && this.maxTime)
        return `Times are limited to ${formattedMinTime} - ${formattedMaxTime}`
      if (this.minTime) return `Times must be after ${formattedMinTime}`
      if (this.maxTime) return `Times must be before ${formattedMaxTime}`
      if (this.invalidHint) return this.invalidHint
      return ''
    },
    calendarIcon(): VuetifyIcon {
      if (this.disabled || this.readonly || this.loading) return '$calendarLock'
      else if (this.menu) return '$calendarRemove'
      return '$calendarTime'
    },
    attachID() {
      return `_${Math.random().toString(36).substr(2, 9)}`
    },
  },
  watch: {
    defaultValue(val) {
      this.time24h = val
    },
    time24h() {
      this.time24h = this.minMaxCheck(this.time24h)
      this.time12h = this.convert24to12(this.time24h)
      this.$emit('change', this.time24h)
    },
  },
  beforeMount() {
    if (this.defaultValue) this.time12h = this.convert24to12(this.time24h)
    this.menu = this.value
  },
  methods: {
    onFocus() {
      this.readonlyHandler()
    },
    onBlur() {
      if (this.readonly) {
        this.colorValue = this.color
      }
    },
    readonlyHandler() {
      this.readonly
        ? (this.colorValue = 'error')
        : (this.colorValue = this.color)
    },
    changeTime12h() {
      if (this.time12h && !this.error) {
        this.time24h = this.convert12to24(this.time12h)
      }
    },
    allowedStep: (m) => m % 5 === 0,
    convert24to12(time) {
      let [hours, minutes] = time.split(':'),
        modifier = 'AM'
      if (hours > 11) modifier = 'PM'
      if (hours === '00') hours = 12
      if (hours > 12) {
        hours = parseInt(hours, 10) - 12
      } else {
        hours = parseInt(hours)
      }
      return `${hours}:${minutes} ${modifier}`
    },
    convert12to24(time12h) {
      const [time, modifier] = time12h.split(' ')
      let [hours, minutes] = time.split(':')
      if (['AM', 'am', 'Am', 'aM'].includes(modifier) && hours === '12')
        hours = '00'
      if (['PM', 'pm', 'Pm', 'pM'].includes(modifier))
        hours = parseInt(hours, 10) + 12
      return `${hours}:${minutes}`
    },
    minMaxCheck(timeCheck) {
      if (!timeCheck) return ''
      // Convert times to dates
      const [hr, mi] = timeCheck.split(':'),
        time = new Date(parseInt(hr), parseInt(mi)),
        [minHr, minMi] = this.minTime.split(':'),
        minTime = new Date(parseInt(minHr), parseInt(minMi)),
        [maxHr, maxMi] = this.maxTime.split(':'),
        maxTime = new Date(parseInt(maxHr), parseInt(maxMi))
      // Time is within Min/Max range
      this.persistentHint = false // Not currently working
      if (time < minTime) {
        this.persistentHint = true
        return this.minTime
      } else if (time > maxTime) {
        this.persistentHint = true
        return this.maxTime
      }
      return timeCheck
    },
  },
})
